import { useRef, useState } from "react"
import type {
  PortalAnswerValue,
  PortalLinkItem,
  PortalLinkUploader,
  PortalLinkViewResponse,
} from "@ledgerbox/domain"

import { errorMessage } from "./api"
import { usePortalApi } from "./api-context"
import { SendWell, UploadQueue } from "./checklist"
import { countInWords, plural } from "./format"
import { amountToSend } from "./item-answers"
import {
  HiddenFileInput,
  InlineError,
  UploadAnnouncer,
  useCoarsePointer,
} from "./ui"
import { useUploadQueue } from "./use-upload-queue"

/**
 * The portal for someone with only the client link. They see what is still
 * open, send files and answer questions. Nothing they sent before, and
 * nothing the firm wrote to them, shows here: that needs a sign-in.
 */
export function LinkView({
  view,
  firmName,
  onViewChange,
  onSignIn,
}: {
  view: PortalLinkViewResponse
  firmName: string
  onViewChange(next: PortalLinkViewResponse): void
  onSignIn(): void
}) {
  const api = usePortalApi()
  const [who, setWho] = useState<{ name: string; email: string }>({
    name: "",
    email: "",
  })
  const [answered, setAnswered] = useState<string[]>([])
  // Read by the upload lane when a file is signed, so a name typed after
  // the page opened still goes with the file.
  const whoRef = useRef(who)
  whoRef.current = who
  const uploader = (): PortalLinkUploader | undefined => {
    const name = whoRef.current.name.trim()
    const email = whoRef.current.email.trim()
    if (!name && !email) return undefined
    return {
      ...(name ? { name } : {}),
      ...(email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? { email } : {}),
    }
  }

  const refresh = async () => onViewChange(await api.openLink())
  const queue = useUploadQueue(
    {
      mint: (body, target) =>
        api.mintLinkUpload({ ...body, uploader: uploader() }, target),
      confirm: api.confirmLinkUpload,
    },
    refresh
  )

  const itemCount = view.requests.reduce(
    (sum, request) => sum + request.items.length,
    0
  )
  const signInCount = view.signInItems.length
  // Items that need a sign-in still wait on the client, so the page never
  // says "nothing waiting" while it lists them below.
  const lede =
    itemCount > 0
      ? itemCount === 1
        ? "One thing is waiting on you. Send it here without signing in."
        : `${capitalize(countInWords(itemCount))} things are waiting on you. Send them here without signing in.`
      : signInCount > 0
        ? `Sign in to finish what ${firmName} still needs. You can send a file below without signing in.`
        : `${firmName} has nothing waiting on you right now. You can still send a file below.`

  return (
    <div className="page checklist">
      <UploadAnnouncer text={queue.announcement} />
      <div className="page-heading">
        <h1>Send files to {firmName}</h1>
        <p className="lede">{lede}</p>
      </div>

      {/* Before the items, so the first file already carries the name. */}
      <section className="panel" aria-labelledby="link-who">
        <div className="panel-head">
          <h2 id="link-who">Who is sending</h2>
          <span className="panel-meta">Optional</span>
        </div>
        <div className="link-who">
          <label>
            <span>Your name</span>
            <input
              value={who.name}
              maxLength={120}
              autoComplete="name"
              onChange={(event) => {
                const name = event.currentTarget.value
                setWho((current) => ({ ...current, name }))
              }}
            />
          </label>
          <label>
            <span>Your email</span>
            <input
              type="email"
              value={who.email}
              maxLength={254}
              autoComplete="email"
              placeholder="you@company.com"
              onChange={(event) => {
                const email = event.currentTarget.value
                setWho((current) => ({ ...current, email }))
              }}
            />
          </label>
          <p className="link-who-note">
            {firmName} sees this beside what you send
          </p>
        </div>
      </section>

      {view.requests.map((request) => (
        <section
          key={request.requestId}
          className="panel"
          aria-labelledby={`link-${request.requestId}`}
        >
          <div className="panel-head">
            <h2 id={`link-${request.requestId}`}>{request.label}</h2>
            <span className="panel-meta tabular">
              {request.items.length}{" "}
              {request.items.length === 1 ? "item" : "items"}
            </span>
          </div>
          <div className="ask-list">
            {request.items.map((item) => (
              <LinkItemRow
                key={item.itemId}
                item={item}
                uploadAllowed={view.uploadAllowed}
                onFiles={(files) =>
                  void queue.handleFiles(files, {
                    requestId: request.requestId,
                    itemId: item.itemId,
                  })
                }
                onAnswer={async (value) => {
                  onViewChange(
                    await api.answerLinkItem(item.itemId, value, uploader())
                  )
                  setAnswered((current) => [...current, item.name])
                }}
              />
            ))}
          </div>
        </section>
      ))}

      {answered.length > 0 ? (
        <p className="scoped-note" role="status">
          {firmName} has your {answered.length === 1 ? "answer" : "answers"}{" "}
          to {answered.map((name) => `“${name}”`).join(", ")}
        </p>
      ) : null}

      {view.uploadAllowed ? (
        <SendWell onFiles={(files) => queue.handleFiles(files)} />
      ) : (
        <div className="notice-card">
          <strong>You cannot add files right now</strong>
          <p>Ask {firmName} to turn uploads back on.</p>
        </div>
      )}

      {queue.queueNote ? (
        <p className="scoped-note">{queue.queueNote}</p>
      ) : null}
      {queue.uploads.length > 0 ? (
        <UploadQueue
          uploads={queue.uploads}
          onRetry={(item) => void queue.uploadOne(item)}
          onDismiss={queue.dismiss}
        />
      ) : null}

      {signInCount > 0 ? (
        <section className="panel" aria-labelledby="link-signin-items">
          <div className="panel-head">
            <h2 id="link-signin-items">Sign in to finish</h2>
            <span className="panel-meta tabular">
              {signInCount} {plural(signInCount, "item", "items")}
            </span>
          </div>
          <ul className="link-signin-items">
            {view.signInItems.map((item) => (
              <li key={item.itemId}>{item.name}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="notice-card link-signin">
        <p>
          {signInCount > 0
            ? "Sign in with a code we email you to finish these and see past files and messages"
            : "To see past files and messages, sign in with a code we email you"}
        </p>
        <button type="button" className="ghost-pill" onClick={onSignIn}>
          Sign in
        </button>
      </div>
    </div>
  )
}

const capitalize = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1)

/** One open item: a file to send, or a short answer. */
function LinkItemRow({
  item,
  uploadAllowed,
  onFiles,
  onAnswer,
}: {
  item: PortalLinkItem
  uploadAllowed: boolean
  onFiles(files: FileList): void
  onAnswer(value: PortalAnswerValue): Promise<void>
}) {
  const fileInput = useRef<HTMLInputElement>(null)
  const photoInput = useRef<HTMLInputElement>(null)
  const coarsePointer = useCoarsePointer()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const send = async (value: PortalAnswerValue) => {
    setPending(true)
    setError(null)
    try {
      await onAnswer(value)
    } catch (failure) {
      setError(
        errorMessage(
          failure instanceof Error
            ? failure
            : new Error("Something went wrong. Try again.")
        )
      )
      setPending(false)
    }
  }

  const upload = item.action === "upload" && uploadAllowed

  return (
    <article className="ask-item" role="group" aria-label={item.name}>
      <span className="tick" aria-hidden="true" />
      <div className="ask-main">
        <b>{item.name}</b>
        {item.description ? <span className="d">{item.description}</span> : null}
        {item.required ? null : <span className="d">Only if you have it.</span>}
        {item.action === "answer" ? (
          <LinkAnswer item={item} pending={pending} onSend={send} />
        ) : null}
        {upload ? (
          <>
            <HiddenFileInput
              ref={fileInput}
              multiple={item.allowMultiple}
              onFiles={onFiles}
            />
            {coarsePointer ? (
              <HiddenFileInput ref={photoInput} camera onFiles={onFiles} />
            ) : null}
          </>
        ) : null}
        {error ? <InlineError message={error} /> : null}
      </div>
      {upload ? (
        <div className="ask-action">
          <button
            type="button"
            className="ghost-pill"
            onClick={() => fileInput.current?.click()}
          >
            {item.allowMultiple ? "Add files" : "Add a file"}
          </button>
          {coarsePointer ? (
            <button
              type="button"
              className="ghost-pill"
              onClick={() => photoInput.current?.click()}
            >
              Take a photo
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  )
}

/** The answer controls a link visitor gets. An answer goes to the firm once;
    changing it later needs a sign-in. */
function LinkAnswer({
  item,
  pending,
  onSend,
}: {
  item: PortalLinkItem
  pending: boolean
  onSend(value: PortalAnswerValue): Promise<void>
}) {
  const [text, setText] = useState("")
  const [picked, setPicked] = useState<string[]>([])
  const [chosen, setChosen] = useState<string | boolean | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  // A link cannot change an answer once it is sent, so a tap only picks and
  // Send sends. A second tap on the picked one clears it.
  if (item.kind === "yes_no" || (item.kind === "choice" && !item.allowMultiple)) {
    const choices: Array<{ value: string | boolean; label: string }> =
      item.kind === "yes_no"
        ? [
            { value: true, label: "Yes" },
            { value: false, label: "No" },
          ]
        : item.options.map((option) => ({ value: option, label: option }))
    // Laid out like a typed answer: the choices, then Send beside them (on
    // a phone, under them), so every item's Send sits in the same place.
    return (
      <div className="answer-field choice-answer">
        <div className="answer-pills" role="group" aria-label={item.name}>
          {choices.map((choice) => (
            <button
              key={String(choice.value)}
              type="button"
              className="choice-pill"
              aria-pressed={chosen === choice.value}
              disabled={pending}
              onClick={() =>
                setChosen((current) =>
                  current === choice.value ? null : choice.value
                )
              }
            >
              {choice.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="ghost-pill"
          disabled={pending || chosen === null}
          onClick={() => {
            if (chosen !== null) void onSend(chosen)
          }}
        >
          {pending ? "Sending…" : "Send"}
        </button>
      </div>
    )
  }

  if (item.kind === "checklist" || item.kind === "choice") {
    const toggle = (option: string) =>
      setPicked((current) =>
        current.includes(option)
          ? current.filter((entry) => entry !== option)
          : item.options.filter(
              (entry) => entry === option || current.includes(entry)
            )
      )
    return (
      <div className="todo-answer">
        <ul className="todo-list" aria-label={item.name}>
          {item.options.map((option) => (
            <li key={option}>
              <label className="todo">
                <input
                  type="checkbox"
                  checked={picked.includes(option)}
                  disabled={pending}
                  onChange={() => toggle(option)}
                />
                <span>{option}</span>
              </label>
            </li>
          ))}
        </ul>
        <div className="keep-answer">
          <button
            type="button"
            className="ghost-pill"
            disabled={pending || picked.length === 0}
            onClick={() => void onSend(picked)}
          >
            {pending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    )
  }

  const submit = () => {
    setProblem(null)
    if (item.kind === "amount") {
      const next = amountToSend(text, "")
      if (next.kind === "invalid") {
        setProblem(next.problem)
        return
      }
      if (next.kind === "send") void onSend(next.value)
      return
    }
    if (text.trim()) void onSend(item.kind === "date" ? text : text.trim())
  }

  return (
    <form
      className="answer-field"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      {item.kind === "text" ? (
        <textarea
          aria-label={item.name}
          maxLength={2000}
          rows={2}
          placeholder="Type your answer"
          value={text}
          disabled={pending}
          onChange={(event) => setText(event.currentTarget.value)}
        />
      ) : (
        <input
          aria-label={item.name}
          type={item.kind === "date" ? "date" : "text"}
          inputMode={item.kind === "amount" ? "decimal" : undefined}
          className={item.kind === "amount" ? "mono" : undefined}
          placeholder={item.kind === "amount" ? "0.00" : undefined}
          maxLength={item.kind === "amount" ? 24 : undefined}
          value={text}
          disabled={pending}
          onChange={(event) => setText(event.currentTarget.value)}
        />
      )}
      <button
        type="submit"
        className="ghost-pill"
        disabled={pending || !text.trim()}
      >
        {pending ? "Sending…" : "Send"}
      </button>
      {problem ? (
        <p className="field-problem" role="alert">
          {problem}
        </p>
      ) : null}
    </form>
  )
}
